import Config from "./config.js";

/**
 * Fields — what goes into a form's hidden inputs, and writing it there.
 *
 * PRECEDENCE, lowest first; a later layer replaces an earlier one for the same field:
 *
 *   1. built-in defaults   utm_medium = "website", country_code = site country,
 *                          current_url / conversion_url = this page
 *   2. options.fields      the caller's values (UTMs included)
 *   3. URL                 utm_* and irclickid → impact_id — this visit's attribution beats any
 *                          value baked into the page
 *   4. options.owner       → direct_assignment_rep, always forced
 *
 * Then, per field, at write time: a value already in the input (a HubSpot default, or what the
 * visitor typed) WINS unless the field is forced; a field the form does not define is skipped —
 * the embed submits nothing else, so adding the hidden field in HubSpot is what switches it on.
 */
const Fields = {};

const UTMS = ["utm_source", "utm_medium", "utm_campaign", "utm_term", "utm_content"];

/** This visit's attribution from a query string: utm_* and impact_id. Empty params are dropped. */
Fields.utms = function (search = window.location?.search ?? "") {
  const params = new URLSearchParams(search);
  const out = {};

  UTMS.forEach((key) => {
    if (params.get(key)) out[key] = params.get(key);
  });

  if (params.get("irclickid")) out.impact_id = params.get("irclickid");

  return out;
};

/**
 * The values to write, and which of them are forced.
 *
 * @param  {object} options  create()'s options — fields, force, owner, utm, country
 * @param  {object} page     { search, href } — the location, injectable for tests
 * @return {{values: object, force: string[]}}
 */
Fields.resolve = function (options = {}, page = window.location ?? {}) {
  const utm = options.utm !== false;
  const values = {};
  const force = Array.isArray(options.force) ? [...options.force] : [];

  if (utm) {
    values.utm_medium = Config.utmMediumDefault();
    values.current_url = page.href || "";
    values.conversion_url = page.href || "";
  }

  if (options.country !== false) values[Config.countryField] = Config.country();

  Object.assign(values, options.fields || {});

  if (utm) Object.assign(values, Fields.utms(page.search || ""));

  if (options.owner) {
    values[Config.ownerField] = options.owner;
    force.push(Config.ownerField);
  }

  return { values, force: options.force === true ? true : force };
};

/**
 * Write values into a rendered form.
 *
 * @param  {HTMLFormElement} form
 * @param  {object}          values  { fieldName: value } — null / undefined / "" are ignored
 * @param  {{force?: string[]|true}} opts  names that overwrite an existing value, or true for all
 * @return {string[]} the names actually written
 */
Fields.fill = function (form, values = {}, { force = [] } = {}) {
  const written = [];

  if (!form?.querySelector) return written;

  Object.entries(values).forEach(([name, value]) => {
    if (value === null || value === undefined || value === "") return;

    const input = form.querySelector(`[name="${name}"]`);

    if (!input) return;

    const forced = force === true || (Array.isArray(force) && force.includes(name));

    // A VISIBLE field is the visitor's to fill — never pre-fill it unless the caller forces it (a
    // referral form's visible city/state would describe the referred business, not the advisor).
    if (!forced && input.type !== "hidden") return;

    if (input.value && !forced) return;

    input.value = String(value);
    // HubSpot tracks its own state off these; a bare .value assignment is not seen.
    input.dispatchEvent(new Event("input", { bubbles: true }));
    input.dispatchEvent(new Event("change", { bubbles: true }));
    written.push(name);
  });

  return written;
};

export default Fields;
